
import { defineComponent, ref, inject, watchEffect } from 'vue';
import { Select, Radio } from '@/components/input-elements';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Notify } from '@/core/services';

export default defineComponent({
    name: 'add new shipping method',
    components: {
        Radio,
        Select
    },

    setup(props, context) {
        const emitter: any = inject('emitter');
        const siteFormRef = ref<null | HTMLFormElement>(null);
        const sites = ref([]) as Record<any, any>;
        const store = useStore();
        const i18n = useI18n();
        const rule = ref([]) as Record<any, any>;
        const optionRule = ref([]) as Record<any, any>;
        const cartRule = ref([]) as Record<any, any>;
        const cartRuleOption = ref([]) as Record<any, any>;
        const siteRules = ref([]) as Record<any, any>;
        const shippingLabel = ref([]) as Record<any, any>;
        const showValidationErrors = ref(false);

        // Given Shipping Form Interface
        const sitesFormData = ref({
            rule: [],
            cartRule: [],
            site_cost_no_rule: [],
            site_tax_no_rule: [],
            sites: [],
            site_cart_rules: [],
            site_cart_rules2: [],
            site_cart_cost: [],
            site_cart_cost2: [],
            site_tax: [],
            site_tax2: [],
            cartRulesData: {},
            noRuleCost: {},
            combinations: {}
        }) as any;

        cartRuleOption.value = [
            {
                label: 'message.WEIGHT message.RULE',
                value: 0,
                default: 0
            },
            {
                label: 'message.TOTAL message.RULE',
                value: 1,
                default: 0
            },
            {
                label: 'message.COMBINATIONS',
                value: 2,
                default: 0
            }
        ];

        optionRule.value = [
            {
                label: 'message.NO message.RULE',
                value: 0
            },
            {
                label: 'message.RULE',
                value: 1
            }
        ];

        watchEffect(() => {
            sites.value = [];
            const getSites = store.getters.getAllSites.data.sites;
            getSites.forEach((element, index) => {
                sites.value.push({
                    label: element.name,
                    value: element.id,
                    index: index
                });
            });
        });

        const getSiteRules = (siteRule: Record<any, any>) => {
            if (Object.entries(sites).length != 0) {
                siteRules.value = [];
                siteRule.forEach(element => {
                    const site = sites.value.find(ele => ele.value == element);
                    siteRules.value.push({
                        relation: 'sites',
                        data: site
                    });
                });
            } else {
                siteRules.value = [];
            }
        };

        const getShippingRuleLabel = e => {
            e.forEach((element, index) => {
                if (element == 1) {
                    shippingLabel.value[index] = `${i18n.t('message.TOTAL')} ${i18n.t('message.RULE')}`;
                } else if (element == 2) {
                    shippingLabel.value[index] = `${i18n.t('message.COMBINATIONS')}`;
                } else {
                    shippingLabel.value[index] = `${i18n.t('message.WEIGHT')} ${i18n.t('message.RULE')}`;
                }
            });
        };

        const addRow = index => {
            if (!sitesFormData.value.combinations) {
                sitesFormData.value.combinations = {};
            }
            if (!sitesFormData.value.combinations[index]) {
                sitesFormData.value.combinations[index] = [];
            }

            // Add a new row to the array
            sitesFormData.value.combinations[index].push({
                firstValue: '',
                secondValue: '',
                price: '',
                tax: false
            });

            // Ensure reactivity
            sitesFormData.value.combinations = { ...sitesFormData.value.combinations };
        };

        const removeRow = (ruleKey, rowIndex) => {
            if (sitesFormData.value.combinations[ruleKey]) {
                // Remove the row at the specified index
                sitesFormData.value.combinations[ruleKey].splice(rowIndex, 1);

                // If the array is empty after removing, delete the key
                if (sitesFormData.value.combinations[ruleKey].length === 0) {
                    delete sitesFormData.value.combinations[ruleKey];
                }

                // Ensure Vue detects the change by reassigning the object
                sitesFormData.value.combinations = { ...sitesFormData.value.combinations };
            }
        };

        const validateForm = () => {
            siteFormRef.value?.validate(valid => {
                if (valid) {
                    const name = [] as Record<any, any>;
                    const validationErrors = [] as Record<any, any>;
                    // Reset validation errors
                    showValidationErrors.value = false;

                    // Validate all rules
                    for (let i = 0; i < siteRules.value.length; i++) {
                        const ruleKey = siteRules.value[i].data.value;
                        const ruleType = rule.value[ruleKey];
                        const cartOption = cartRule.value[ruleKey];

                        if (ruleType === 1) {
                            if (cartOption === 2) {
                                // Validate combinations data
                                if (sitesFormData.value.combinations[ruleKey]) {
                                    const combinationsForRule = sitesFormData.value.combinations[ruleKey];

                                    // Validate each row for required values and ordering
                                    for (let j = 0; j < combinationsForRule.length; j++) {
                                        const row = combinationsForRule[j];
                                        const first = Number(row.firstValue);
                                        const second = Number(row.secondValue);
                                        const price = Number(row.price);

                                        if (!first || first <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `First value must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        if (!second || second <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Second value must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        if (!price || price <= 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Price must be greater than 0 in row ${j + 1}`
                                            });
                                        }

                                        // Check if second value is greater than first value
                                        if (second <= first && first > 0 && second > 0) {
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: j,
                                                message: `Second value must be greater than first value in row ${j + 1}`
                                            });
                                        }
                                    }

                                    // Prepare only valid rows for overlap checking (skip incomplete rows)
                                    const validRows = combinationsForRule
                                        .filter(r => Number(r.firstValue) > 0 && Number(r.secondValue) > Number(r.firstValue))
                                        .slice()
                                        .sort((a, b) => Number(a.firstValue) - Number(b.firstValue));

                                    // Check for overlapping ranges among valid rows only
                                    for (let j = 0; j < validRows.length - 1; j++) {
                                        const currentRow = validRows[j];
                                        const nextRow = validRows[j + 1];
                                        if (Number(currentRow.secondValue) >= Number(nextRow.firstValue)) {
                                            const currentIndex = combinationsForRule.indexOf(currentRow);
                                            validationErrors.push({
                                                ruleKey,
                                                rowIndex: currentIndex,
                                                message: `Range overlaps with next combination in row ${currentIndex + 1}`
                                            });
                                        }
                                    }
                                } else {
                                    validationErrors.push({
                                        ruleKey,
                                        message: `${i18n.t('message.PLEASE_ADD_AT_LEAST_ONE_COMBINATION_FOR')} ${siteRules.value[i].data.label}`
                                    });
                                }
                            } else if (cartOption === 0 || cartOption === 1) {
                                // Validate weight/total rules
                                if (
                                    typeof sitesFormData.value.site_cart_rules[ruleKey] === 'undefined' ||
                                    sitesFormData.value.site_cart_rules[ruleKey] == '' ||
                                    typeof sitesFormData.value.site_cart_rules2[ruleKey] === 'undefined' ||
                                    sitesFormData.value.site_cart_rules2[ruleKey] == '' ||
                                    typeof sitesFormData.value.site_cart_cost[ruleKey] === 'undefined' ||
                                    sitesFormData.value.site_cart_cost[ruleKey] == '' ||
                                    typeof sitesFormData.value.site_cart_cost2[ruleKey] === 'undefined' ||
                                    sitesFormData.value.site_cart_cost2[ruleKey] == ''
                                ) {
                                    name.push(siteRules.value[i].data.label);
                                }
                            }
                        } else {
                            // Validate no rule option
                            if (typeof sitesFormData.value.site_cost_no_rule[ruleKey] === 'undefined' || sitesFormData.value.site_cost_no_rule[ruleKey] == '') {
                                name.push(siteRules.value[i].data.label);
                            }
                        }
                    }

                    // Show validation errors if any
                    if (validationErrors.length > 0 || name.length > 0) {
                        showValidationErrors.value = true;

                        // Show combination errors first
                        validationErrors.forEach(error => {
                            Notify.error(error.message);
                        });

                        // Then show other rule errors
                        if (name.length > 0) {
                            Notify.error(`${i18n.t('message.ENTER_RULES_AND_PRICE_FOR_SELECTED')} ${name.join(' , ')} ${i18n.t('message.SHIPPING')}`);
                        }

                        return;
                    }

                    // Prepare data for submission
                    const cartRulesData = {
                        sites: {
                            greater_condition: {
                                rules: sitesFormData.value.site_cart_rules,
                                cost: sitesFormData.value.site_cart_cost,
                                tax: sitesFormData.value.site_tax
                            },
                            less_condition: {
                                rules: sitesFormData.value.site_cart_rules2,
                                cost: sitesFormData.value.site_cart_cost2,
                                tax: sitesFormData.value.site_tax2
                            },
                            combinations: {
                                combinations: sitesFormData.value.combinations
                            }
                        }
                    };

                    sitesFormData.value.cartRulesData = cartRulesData;
                    const noRuleCost = {
                        site_cost: sitesFormData.value.site_cost_no_rule,
                        site_tax: sitesFormData.value.site_tax_no_rule
                    };

                    sitesFormData.value.noRuleCost = noRuleCost;
                    sitesFormData.value.rule = rule.value;
                    sitesFormData.value.cartRule = cartRule.value;

                    context.emit('store', 'sitesFormData', sitesFormData.value);
                    return true;
                }

                return false;
            });
        };

        const resetForm = () => {
            emitter.emit('clearInput');
            siteFormRef.value?.resetFields();
        };

        const editShippingMethodValue = (shippingData: Record<any, any>) => {
            resetForm();
            if (shippingData.sites.length > 0) {
                shippingData.sites.forEach(element => {
                    sitesFormData.value.sites.push(element.id);
                    siteRules.value.push({
                        relation: 'sites',
                        data: { label: element.name, value: element.id }
                    });

                    shippingData.shipping_rule.forEach(ele => {
                        if (ele.relation == 'site' && ele.relation_id == element.id) {
                            switch (ele.condition) {
                                case '==':
                                    rule.value[element.id] = 0;
                                    sitesFormData.value.site_cost_no_rule[element.id] = ele.shipping_cost.cost;
                                    sitesFormData.value.site_tax_no_rule[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    break;
                                case '>=':
                                    rule.value[element.id] = 1;
                                    cartRule.value[element.id] = ele.rule == 'total' ? 1 : 0;
                                    sitesFormData.value.site_cart_cost[element.id] = ele.shipping_cost.cost;
                                    sitesFormData.value.site_tax[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    sitesFormData.value.site_cart_rules[element.id] = ele.cart_rule_value;
                                    break;
                                case '<':
                                    sitesFormData.value.site_cart_cost2[element.id] = ele.shipping_cost.cost;
                                    sitesFormData.value.site_tax2[element.id] = ele.shipping_cost.taxable == 0 ? false : true;
                                    sitesFormData.value.site_cart_rules2[element.id] = ele.cart_rule_value;
                                    break;
                                case '<>':
                                    rule.value[element.id] = 1;
                                    cartRule.value[element.id] = ele.rule == 'between' ? 2 : 0;

                                    // Ensure combinations[element.id] exists as an array before pushing
                                    if (!sitesFormData.value.combinations[element.id]) {
                                        sitesFormData.value.combinations[element.id] = [];
                                    }

                                    // Push new condition to the array
                                    sitesFormData.value.combinations[element.id].push({
                                        firstValue: ele.first_value,
                                        secondValue: ele.second_value,
                                        price: ele.shipping_cost.cost,
                                        tax: ele.shipping_cost.taxable == 0 ? false : true
                                    });
                                    break;
                            }
                        }
                    });
                });
            }
        };

        return {
            sitesFormData,
            sites,
            optionRule,
            rule,
            cartRule,
            cartRuleOption,
            shippingLabel,
            siteRules,
            siteFormRef,
            editShippingMethodValue,
            validateForm,
            getShippingRuleLabel,
            getSiteRules,
            addRow,
            removeRow,
            showValidationErrors
        };
    }
});
